import {
  DATE_FORMATTER_STR,
  DateRange,
  DateRangeInput,
  Filters,
  formatDate,
  Header,
  isBefore,
  ScrollContainer,
  Search,
  Select,
  StatsTeam,
  useBreakpoints,
  useLoading,
  ValueOpt,
} from "best-common-react";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { getAdminSchedule } from "../../../api/RequesTixApi";
import EventsTable from "../../../components/events/EventsTable";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { AdminGameDTO } from "../../../types/Game";
import { nextMonthStr, todayStr } from "../../../util/DateObjUtil";
import { useUrlParams } from "../../../util/RouteUtil";
import { getTeamOption } from "../../../util/TeamUtil";

const AdminEvents = () => {
  const location = useLocation();
  const { teamsOptions } = useDropdowns();
  const { loading, setLoadingNonBlocking } = useLoading();
  const { isMobile } = useBreakpoints();
  const [events, setEvents] = React.useState<AdminGameDTO[]>([]);

  const [{ startDate, endDate, teamId, searchTerm }, setParam] = useUrlParams({
    startDate: todayStr,
    endDate: nextMonthStr,
    teamId: undefined,
    searchTerm: "",
  });

  const dateRange: DateRange<Date> = {
    start: new Date(Date.parse(startDate!)),
    end: new Date(Date.parse(endDate!)),
  };

  const selectedHomeTeam = useMemo(() => getTeamOption(teamId, teamsOptions), [teamId, teamsOptions]);

  const updateDate = useCallback(
    (dateRange: DateRange<Date>) => {
      setParam({
        startDate: formatDate(dateRange.start!, DATE_FORMATTER_STR),
        endDate: formatDate(dateRange.end!, DATE_FORMATTER_STR),
      });
    },
    [setParam],
  );

  const fetchEvents = useCallback(async () => {
    try {
      if (isBefore(startDate!, endDate!)) {
        setLoadingNonBlocking(true);
        const res: AdminGameDTO[] = await getAdminSchedule(teamId ? Number(teamId) : 0, null, startDate!, endDate!);
        setEvents(res);
      }
    } catch (e) {
      console.error(e);
      setEvents([]);
    } finally {
      setLoadingNonBlocking(false);
    }
  }, [startDate, endDate, teamId, setLoadingNonBlocking]);

  useEffect(() => {
    void fetchEvents();
  }, [fetchEvents]);

  return (
    <div>
      <Header sticky title="Events" flexColumn>
        <Filters actionIconPosition="absolute" numberDisplayed={isMobile ? 1 : 3}>
          <DateRangeInput
            id="event-dates"
            label="Event Date"
            value={dateRange}
            onChange={updateDate}
            maxRange={{
              months: 1,
            }}
            withPortal
            gutterBottom
            popoverPlacement="bottom-end"
          />
          <Select
            id="hometeam-dropdown"
            label="Home Team"
            options={teamsOptions}
            value={selectedHomeTeam}
            onChange={(team: ValueOpt<StatsTeam>) => setParam("teamId", String(team.value.id))}
            gutterBottom
          />
          <Search
            id="search-box"
            label="Search Events"
            placeholder="begin typing to filter events"
            value={searchTerm}
            onChange={(value: string) => setParam("searchTerm", value)}
            gutterBottom
          />
        </Filters>
      </Header>
      <ScrollContainer className="bcr-p-2">
        <EventsTable values={events} searchTerm={searchTerm ?? ""} location={location} loading={loading} />
      </ScrollContainer>
    </div>
  );
};

export default AdminEvents;
